import { action, autorun, observable, reaction } from "mobx";
import { observer } from "mobx-react-lite";
import type {
  FC,
  ForwardedRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  Ref,
  RefAttributes,
} from "react";
import {
  createContext,
  createElement,
  forwardRef,
  memo,
  PureComponent,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { configuration } from "./configure.ts";
import type { Constructable } from "./types.ts";
import type { ViewModel } from "./ViewModel.ts";
import { OBJECT, PARENT, PROTOTYPE, REFLECT, VIEW_PROPS } from "./ViewModel.ts";

declare const __DEV__: boolean;

type TForwardRefComponent = {
  $$typeof?: symbol;
};

type TViewModelRuntime = {
  [VIEW_PROPS]: unknown;
  d: Array<() => void>;
  [key: string]: unknown;
};

type TLifecycleMethodName =
  | "onViewMounted"
  | "onViewUpdated"
  | "onViewUnmounted"
  | "onViewMountedSync"
  | "onViewUpdatedSync"
  | "onViewUnmountedSync";

const isDevelopment = import.meta.env.MODE === "development";
const devScope = globalThis as typeof globalThis & { __DEV__?: boolean };
devScope.__DEV__ = isDevelopment;

const ViewModelContext = createContext<ViewModel | null>(null);

const onViewMounted = "onViewMounted";
const onViewUpdated = "onViewUpdated";
const onViewUnmounted = "onViewUnmounted";

const onViewMountedSync = "onViewMountedSync";
const onViewUpdatedSync = "onViewUpdatedSync";
const onViewUnmountedSync = "onViewUnmountedSync";

const callLifecycle = (
  viewModel: TViewModelRuntime,
  methodName: TLifecycleMethodName,
) => {
  const method = viewModel[methodName];
  if (typeof method === "function") {
    (method as () => void).call(viewModel);
  }
};

const createComponent = <P, V extends ViewModel, R>(
  component: FVC<P, V, R>,
  vmFactory: (props?: P) => ViewModel | null,
  options: TViewOptions<P> = {},
  vmName?: string,
) => {
  const isForwardRef =
    (component as TForwardRefComponent).$$typeof ===
    Symbol.for("react.forward_ref");

  const render = (props: P, ref?: Ref<R>) => {
    const viewModel = vmFactory(props);
    const observedComponent = useState(() =>
      options.observer === false ? component : observer(component),
    )[0];

    const componentProps = {
      ...props,
      viewModel,
      ...(isForwardRef ? { ref } : {}),
    } as PropsWithVM<P, V> & { ref?: Ref<R> };

    let element: ReactElement = createElement(
      observedComponent as unknown as FC<unknown>,
      componentProps as Record<string, unknown>,
    );

    if (vmName) {
      element = createElement(
        ViewModelContext.Provider,
        { value: viewModel },
        element,
      );
    }

    return createElement(configuration.Wrapper, null, element);
  };

  let Component = ((props: P) => render(props)) as FCWithRef<P, R>;

  if (isForwardRef) {
    const forwardRender = (props: PropsWithoutRef<P>, ref: ForwardedRef<R>) =>
      render(props as P, ref);
    Component = forwardRef(forwardRender) as unknown as FCWithRef<P, R>;
  }

  Component = memo(Component, options.propsAreEqual) as FCWithRef<P, R>;

  if (__DEV__) {
    Component.displayName = `${!vmName ? "Child" : ""}View${vmName ? `@${vmName}` : ""}`;
  }

  return Component;
};

export type TViewOptions<T> = {
  observer?: boolean;
  propsAreEqual?: (prevProps: Readonly<T>, nextProps: Readonly<T>) => boolean;
};

export type FCWithRef<P, R> = FC<
  P & (R extends HTMLElement ? RefAttributes<R> : unknown)
>;

export type BaseComponent<P, V, R> = FCWithRef<P & { viewModel: V }, R>;

const useLifeCycle = (
  useHook: typeof useEffect,
  viewModel: TViewModelRuntime,
  onUpdated: TLifecycleMethodName,
  onMounted: TLifecycleMethodName,
  onUnmounted: TLifecycleMethodName,
  updateCb: (() => void) | null,
  unmountCb?: () => void,
) => {
  const wasRendered = useRef(false);

  useHook(() => {
    if (wasRendered.current) {
      if (updateCb) {
        updateCb();
      }
      callLifecycle(viewModel, onUpdated);
    }

    wasRendered.current = true;
  });

  useHook(() => {
    callLifecycle(viewModel, onMounted);

    return () => {
      if (unmountCb) {
        unmountCb();
      }
      callLifecycle(viewModel, onUnmounted);
    };
  }, []);
};

/**
 * HOC-function that creates an instance of View. The View creates an instance of the ViewModel; passes its parent;
 * updates viewProps every time the View is re-rendered. Views are memoized with {@link React.memo}.
 * @param VM - ViewModel class.
 *
 * @example
 * type Props = {
 *   prop1: number;
 *   prop2: string;
 * }
 *
 * const SomeView = view(SomeViewModel)<Props>(({ viewModel, prop1, prop2 }) => (
 *   JSX here
 * ));
 */
export const view =
  <V extends ViewModel>(VM: Constructable<V>) =>
  <P, R extends HTMLElement | unknown = unknown>(
    ViewComponent: FVC<P, V, R>,
    options?: TViewOptions<P>,
  ): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<R>> =>
    createComponent(
      ViewComponent,
      (props) => {
        const parentViewModel = useContext(ViewModelContext);
        const prototypeContext = PROTOTYPE as unknown as Record<
          string,
          unknown
        >;
        const viewModel = useState(() => {
          prototypeContext[PARENT] = parentViewModel;
          prototypeContext[VIEW_PROPS] = props as Readonly<unknown>;
          const vm = configuration.vmFactory(VM);
          delete prototypeContext[PARENT];
          delete prototypeContext[VIEW_PROPS];
          return vm;
        })[0];
        const runtimeViewModel = viewModel as unknown as TViewModelRuntime;

        useLifeCycle(
          useEffect,
          runtimeViewModel,
          onViewUpdated,
          onViewMounted,
          onViewUnmounted,
          null,
          () => {
            runtimeViewModel.d = runtimeViewModel.d.filter(
              (dispose: () => void) => {
                dispose();
                return false;
              },
            );
          },
        );

        useLifeCycle(
          useLayoutEffect,
          runtimeViewModel,
          onViewUpdatedSync,
          onViewMountedSync,
          onViewUnmountedSync,
          action(() => {
            runtimeViewModel[VIEW_PROPS] = props;
            return runtimeViewModel[VIEW_PROPS];
          }),
        );

        return viewModel;
      },
      options,
      VM.name || "Anonymous",
    ) as unknown as ForwardRefExoticComponent<
      PropsWithoutRef<P> & RefAttributes<R>
    >;

/**
 * HOC-function that creates an instance of ChildView. ChildView doesn't create or affect on the ViewModel, it only
 * uses one. ChildViews are memoized with {@link React.memo}.
 */
export const childView =
  <V extends ViewModel>() =>
  <P, R extends HTMLElement | unknown = unknown>(
    ChildViewComponent: FVC<P, V, R>,
    options?: TViewOptions<P>,
  ): FCWithRef<P, R> =>
    createComponent(
      ChildViewComponent,
      () => useContext(ViewModelContext),
      options,
    );

for (const [source, name] of [
  [autorun, "autorun"] as const,
  [reaction, "reaction"] as const,
]) {
  const mobxRunner = source as unknown as (...args: unknown[]) => () => void;
  const runtimePrototype = PROTOTYPE as unknown as {
    autorun: (...args: unknown[]) => () => void;
    reaction: (...args: unknown[]) => () => void;
  };

  runtimePrototype[name] = function (
    this: TViewModelRuntime,
    ...args: unknown[]
  ) {
    const runtimeViewModel = this as unknown as TViewModelRuntime;
    const disposer = mobxRunner(...args);
    return runtimeViewModel.d[runtimeViewModel.d.push(disposer) - 1];
  };
}

const metadataDecorator: PropertyDecorator =
  typeof REFLECT.metadata === "function"
    ? REFLECT.metadata("design:type", OBJECT)
    : () => undefined;

if (typeof REFLECT.decorate === "function") {
  REFLECT.decorate([observable.ref, metadataDecorator], PROTOTYPE, VIEW_PROPS);
}

/**
 * A class with which you can create a ChildView. The context of this class is equals to a view model. And also
 * there's a typed getter viewModel, which is just an alias of the context field.
 */
export class ChildViewComponent<
  V,
  P = unknown,
  S = unknown,
  SS = unknown,
> extends PureComponent<P, S, SS> {
  static contextType = ViewModelContext;

  get viewModel(): V {
    return this.context as V;
  }
}

export type ViewAsProps<V extends ViewModel> = Omit<
  V,
  "d" | "parent" | "viewProps"
>;

export type FVC<P, V extends ViewModel, R = unknown> = BaseComponent<
  P,
  ViewAsProps<V>,
  R
>;

export type PropsWithVM<P, V extends ViewModel> = P & {
  viewModel: ViewAsProps<V>;
};
